import { useEffect, useRef, useState } from "react";
import { motion, animate, useInView, useReducedMotion } from "framer-motion";

const EASE = [0.22, 1, 0.36, 1];

export function FadeUp({ children, delay = 0, className = "", as = "div", ...rest }) {
  const Comp = motion[as];
  return (
    <Comp initial={{ opacity: 0, y: 28 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.9, ease: EASE, delay }} className={className} {...rest}>
      {children}
    </Comp>
  );
}

export function MaskedLines({ lines, className = "", lineClassName = "", delay = 0, inView = false, as = "h1" }) {
  const Comp = motion[as];
  const trigger = inView
    ? { initial: "hidden", whileInView: "show", viewport: { once: true, amount: 0.1 } }
    : { initial: "hidden", animate: "show" };
  return (
    <Comp className={className} {...trigger} transition={{ staggerChildren: 0.11, delayChildren: delay }}>
      {lines.map((line, i) => (
        <span key={i} className={`block overflow-hidden pb-[0.08em] ${lineClassName}`}>
          <motion.span className="block" variants={{ hidden: { y: "105%" }, show: { y: "0%" } }}
            transition={{ duration: 1.05, ease: EASE }}>
            {line}
          </motion.span>
        </span>
      ))}
    </Comp>
  );
}

export function Counter({ value = 0, prefix = "", suffix = "", decimals = 0, className = "", testId }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.4 });
  const reduce = useReducedMotion();
  const fmt = (n) => Number(n).toLocaleString("en-US", { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
  const [display, setDisplay] = useState(fmt(value));
  useEffect(() => {
    if (!inView || reduce) { setDisplay(fmt(value)); return undefined; }
    const controls = animate(0, Number(value) || 0, { duration: 1.8, ease: EASE, onUpdate: (v) => setDisplay(fmt(v)) });
    return () => controls.stop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inView, value, reduce, decimals]);
  return (
    <span ref={ref} className={`tabular-nums ${className}`} data-testid={testId}>
      {prefix}{display}{suffix}
    </span>
  );
}

export function Marquee({ items, className = "" }) {
  const row = [...items, ...items];
  return (
    <div className={`relative overflow-hidden ${className}`} aria-hidden="true">
      <div className="flex w-max animate-marquee items-center motion-reduce:animate-none">
        {row.map((t, i) => (
          <span key={i} className="flex items-center whitespace-nowrap">
            <span className="px-6 sm:px-10">{t}</span>
            <span className="h-2 w-2 rotate-45 bg-electric/80" />
          </span>
        ))}
      </div>
    </div>
  );
}

export function SectionHead({ eyebrow, title, sub, className = "", align = "left" }) {
  return (
    <div className={`${align === "center" ? "mx-auto text-center" : ""} max-w-3xl ${className}`}>
      {eyebrow && <FadeUp><p className="eyebrow mb-5">{eyebrow}</p></FadeUp>}
      <MaskedLines as="h2" inView lines={Array.isArray(title) ? title : [title]}
        className="text-[2.1rem] leading-[1.02] sm:text-5xl lg:text-[3.6rem] font-semibold tracking-[-0.035em] text-white" />
      {sub && <FadeUp delay={0.15}><p className="mt-6 text-base sm:text-lg leading-relaxed text-slate-400 max-w-2xl">{sub}</p></FadeUp>}
    </div>
  );
}
